import { useState } from 'react';

import axios, { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { useFetchData } from '../../../hooks/useFetchData';
import {
  OutreachConfigFormFields,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachMetadata,
  OutreachSubscriberConfigResponse,
} from '../../Monitor/types';

import {
  outreachConfigEmailAtom,
  outreachConfigPushAtom,
  outreachConfigWebhookAtom,
  outreachMetadataAtom,
} from '../../../state';
import { DependencyContainer } from '../../../DependencyContainer';
import { FormikSubscriberOutreachTriggerValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/subscriber/FormikSubscriberOutreachTriggerField';
import { FormikOutreachConfigValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/FormikOutreachConfigField';
import { FormikOutreachSupportTimeValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/support/FormikOutreachSupportTimeField';
import { FormikSupportOutreachTriggerValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/support/types';
import { FormikSupportOutreachContentValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/support/FormikSupportOutreachContentField';
import { FormikOutreachSubscriberTimeValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/subscriber/FormikOutreachSubscriberTimeField';
import { FormikSubscriberOutreachContentValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/subscriber/FormikSubscriberOutreachContentField';
import {
  prepareFormData,
  preparePostPayloadForSubscriber,
  preparePostPayloadForSupport,
  preparePostPayloadForSupportWebhook,
} from '../../Monitor/components/OutreachConfigForm/outreach-config-marshaller';
import { getFormDefaultValues } from '../helpers/outreach-config-form-helper';
import { FormikSupportOutreachClearMessageValue } from '../../Monitor/components/OutreachConfigForm/controls/OutreachConfig/support/FormikSupportOutreachClearMessageField';
import { OutreachTypes } from '../../../urls';
import { Maybe } from '../../../types';
import { useFeaturePermission } from '../../../hooks/useFeaturePermission';
import { Permission } from '../../../userPermissionConfig';
import { ErrorResponse } from '../../../types';

export type UseOutreachValues = {
  loadingMetadata: boolean;
  isError: boolean;
  getFormInitialValues: () => OutreachConfigFormDto;
  onSubmit: (values: OutreachConfigFormDto) => void;
  isSubmitting: boolean;
  flushConfig: () => void;
};

type UseOutreachParams = {
  subscriber: {
    onCreateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onCreateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
  };
  supportEmail: {
    onCreateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onCreateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
  };
  supportWebhook: {
    onCreateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onCreateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateSuccess: (
      outreachType: OutreachTypes,
      values: OutreachConfigFormDto,
    ) => void;
    onUpdateFailure: (
      outreachType: OutreachTypes,
      error: AxiosError<ErrorResponse>,
      values: OutreachConfigFormDto,
    ) => void;
  };
  // this callback is called only when either create or update is successful
  onFinalize: () => void;
  onRetrieveConfigError: (error: AxiosError<ErrorResponse>) => void;
};

const { metadataService, outreachService } = new DependencyContainer();

export type OutreachConfigFormDto = {
  outreachConfig: FormikOutreachConfigValue;
  //support
  supportOutreachTime: FormikOutreachSupportTimeValue;
  supportOutreachTrigger: FormikSupportOutreachTriggerValue;
  supportOutreachContent: FormikSupportOutreachContentValue;
  supportOutreachClearMessage?: FormikSupportOutreachClearMessageValue;

  //webhook
  supportOutreachTriggerWebhook: FormikSupportOutreachTriggerValue;
  supportOutreachTimeWebhook: FormikOutreachSupportTimeValue;

  // subscriber
  subscriberOutreachTime: FormikOutreachSubscriberTimeValue;
  subscriberOutreachTrigger: FormikSubscriberOutreachTriggerValue;
  subscriberOutreachContent: FormikSubscriberOutreachContentValue;
  // @TODO: Leaving for now same type as for supports
  subscriberOutreachClearMessage: { subject: string; message: string };
};

const useOutreach = (
  params?: Partial<UseOutreachParams>,
): UseOutreachValues => {
  const { monitorId, partnerId } = useParams<{
    monitorId: string;
    partnerId: string;
  }>();
  const [metadata, setMetadata] = useRecoilState(outreachMetadataAtom);
  const [configEmail, setConfigEmail] = useRecoilState(outreachConfigEmailAtom);
  const [configWebhook, setConfigWebhook] = useRecoilState(
    outreachConfigWebhookAtom,
  );
  const [configPush, setConfigPush] = useRecoilState(outreachConfigPushAtom);
  const { hasPermission } = useFeaturePermission();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMetadata, metadataError] = useFetchData(
    () => metadataService.getOutreachMetadata(partnerId, monitorId),
    (data: OutreachMetadata) => {
      return (
        setMetadata(data),
        [partnerId, monitorId],
        () => !partnerId || !monitorId
      );
    },
  );
  const [loadingOutreachConfigEmail] = useFetchData<OutreachConfigResponse[]>(
    async () => {
      if (!partnerId || !monitorId) {
        return;
      }
      return await outreachService.retrieveOutreachEmailConfig(
        partnerId,
        monitorId,
      );
    },
    (data) => {
      setConfigEmail(data);
    },
    [partnerId, monitorId],
    () => !partnerId || !monitorId,
    params?.onRetrieveConfigError,
  );

  const [loadingOutreachConfigWebhook] = useFetchData<
    OutreachConfigResponseWebhook[]
  >(
    async () => {
      if (!partnerId || !monitorId) {
        return;
      }
      return await outreachService.retrieveOutreachWebhookConfig(
        partnerId,
        monitorId,
      );
    },
    (data) => {
      setConfigWebhook(data);
    },
    [partnerId, monitorId],
    () => !partnerId || !monitorId,
    params?.onRetrieveConfigError,
  );

  const [loadingOutreachConfigPush] = useFetchData<
    OutreachSubscriberConfigResponse[]
  >(
    async () => {
      if (!partnerId || !monitorId) {
        return;
      }
      return await outreachService.retrieveOutreachPushConfig(
        partnerId,
        monitorId,
      );
    },
    (data) => {
      setConfigPush(data);
    },
    [partnerId, monitorId],
    () => !partnerId || !monitorId,
    params?.onRetrieveConfigError,
  );
  const isError = Boolean(metadataError);

  const getFormInitialValues = (): OutreachConfigFormDto => {
    const defaults = getFormDefaultValues();
    if (!partnerId || !monitorId || !metadata) {
      return defaults;
    }
    if (
      loadingOutreachConfigEmail ||
      loadingOutreachConfigWebhook ||
      loadingOutreachConfigPush ||
      !configEmail ||
      !configWebhook ||
      !configPush ||
      !configPush[0] === undefined ||
      !metadata
    ) {
      return defaults;
    }
    const formData = prepareFormData(
      configEmail[0],
      configWebhook[0],
      defaults,
      configPush[0],
    );
    return formData;
  };

  const getOutreachId = (outreachType: OutreachTypes): Maybe<string> => {
    const mapOutreachTypeToConfig = {
      [OutreachTypes.Email]: configEmail,
      [OutreachTypes.Webhooks]: configWebhook,
      [OutreachTypes.Push]: configPush,
    };
    const config = mapOutreachTypeToConfig[outreachType];
    if (!config) {
      return undefined;
    }
    return config[0]?.outreachId || undefined;
  };

  const handleActionsForSupportConfig = async (
    values: OutreachConfigFormDto,
  ) => {
    const outreachSupportId = getOutreachId(OutreachTypes.Email);

    try {
      const payload = preparePostPayloadForSupport(values);
      const issueMessageEmails = payload.issueMessageConfig.recipients.filter(
        (e) => e !== '',
      );
      const clearMessageEmails = payload.clearMessageConfig?.recipients.filter(
        (e) => e !== '',
      );
      let payloadWithEmails = {
        ...payload,
        issueMessageConfig: {
          ...payload.issueMessageConfig,
          recipients: issueMessageEmails,
        },
      };
      if (payloadWithEmails.clearMessageConfig && clearMessageEmails) {
        payloadWithEmails = {
          ...payloadWithEmails,
          clearMessageConfig: {
            ...payloadWithEmails.clearMessageConfig,
            recipients: clearMessageEmails,
          },
        };
      }
      if (outreachSupportId) {
        await outreachService.updateOutreachEmailConfig(
          partnerId,
          monitorId,
          outreachSupportId,
          payloadWithEmails,
        );
        params?.supportEmail?.onUpdateSuccess?.(OutreachTypes.Email, values);
      } else {
        await outreachService.saveOutreachEmailConfig(
          partnerId,
          monitorId,
          payloadWithEmails,
        );
        params?.supportEmail?.onCreateSuccess?.(OutreachTypes.Email, values);
      }
    } catch (error) {
      const strategy: keyof UseOutreachParams['supportEmail'] = outreachSupportId
        ? 'onUpdateFailure'
        : 'onCreateFailure';
      const handler = params?.supportEmail![strategy];
      if (axios.isAxiosError(error))
        handler?.(OutreachTypes.Email, error, values);
      throw error;
    }
  };

  const handleActionsForSupportWebhookConfig = async (
    values: OutreachConfigFormDto,
  ) => {
    const outreachSupportWebhookId = getOutreachId(OutreachTypes.Webhooks);

    try {
      const payload = preparePostPayloadForSupportWebhook(values);
      let payloadWithWebhooks = {
        ...payload,
        issueMessageConfig: {
          ...payload.issueMessageConfig,
        },
      };
      if (outreachSupportWebhookId) {
        await outreachService.updateOutreachWebhookConfig(
          partnerId,
          monitorId,
          outreachSupportWebhookId,
          payloadWithWebhooks,
        );
        params?.supportWebhook?.onUpdateSuccess?.(
          OutreachTypes.Webhooks,
          values,
        );
      } else {
        await outreachService.saveOutreachWebhookConfig(
          partnerId,
          monitorId,
          payloadWithWebhooks,
        );
        params?.supportWebhook?.onCreateSuccess?.(
          OutreachTypes.Webhooks,
          values,
        );
      }
    } catch (error) {
      const strategy: keyof UseOutreachParams['supportWebhook'] = outreachSupportWebhookId
        ? 'onUpdateFailure'
        : 'onCreateFailure';
      const handler = params?.supportWebhook![strategy];
      if (axios.isAxiosError(error))
        handler?.(OutreachTypes.Webhooks, error, values);
      throw error;
    }
  };

  const getPayloadForSubscriberConfig = async (
    values: OutreachConfigFormDto,
  ) => {
    const subscriberOutreachPayload = preparePostPayloadForSubscriber(values);
    let finalSubscriberPayload = subscriberOutreachPayload;
    return finalSubscriberPayload;
  };

  const flushConfig = () => {
    setConfigEmail([]);
    setConfigPush([]);
    setConfigWebhook([]);
  };

  const handlePotentialDeleteAction = async (values: OutreachConfigFormDto) => {
    const outreachSubscriberId = getOutreachId(OutreachTypes.Push);
    const outreachSupportId = getOutreachId(OutreachTypes.Email);
    const outreachWebhookId = getOutreachId(OutreachTypes.Webhooks);

    if (
      outreachWebhookId &&
      !values.outreachConfig[OutreachConfigFormFields.WebhookOutreach]
    ) {
      // deleting support webhook config
      await outreachService.deleteOutreachWebhookConfig(
        partnerId,
        monitorId,
        outreachWebhookId,
      );
    }

    if (
      outreachSupportId &&
      !values.outreachConfig[OutreachConfigFormFields.SupportOutreach]
    ) {
      // deleting support email config
      await outreachService.deleteOutreachEmailConfig(
        partnerId,
        monitorId,
        outreachSupportId,
      );
    }

    if (
      outreachSubscriberId &&
      !values.outreachConfig[OutreachConfigFormFields.SubscriberOutreach]
    ) {
      // deleting subscriber config email
      await outreachService.deleteOutreachPushConfig(
        partnerId,
        monitorId,
        outreachSubscriberId,
      );
    }
    flushConfig();
  };

  const handleActionsForSubscriberConfig = async (
    values: OutreachConfigFormDto,
  ) => {
    const outreachSubscriberId = getOutreachId(OutreachTypes.Push);
    try {
      const payload = await getPayloadForSubscriberConfig(values);

      if (outreachSubscriberId) {
        await outreachService.updateOutreachPushConfig(
          partnerId,
          monitorId,
          outreachSubscriberId,
          payload,
        );
        params?.subscriber?.onUpdateSuccess?.(OutreachTypes.Push, values);
      } else {
        await outreachService.saveOutreachPushConfig(
          partnerId,
          monitorId,
          payload,
        );
        params?.subscriber?.onCreateSuccess?.(OutreachTypes.Push, values);
      }
    } catch (error) {
      const strategy: keyof UseOutreachParams['subscriber'] = outreachSubscriberId
        ? 'onUpdateFailure'
        : 'onCreateFailure';
      const handler = params?.subscriber![strategy];
      if (axios.isAxiosError(error))
        handler?.(OutreachTypes.Push, error, values);
      throw error;
    }
  };

  const onSubmit = async (values: OutreachConfigFormDto) => {
    const canEditSupportEmail = hasPermission(
      Permission.EditSupportEmailOutreach,
    );
    const canEditSupportWebhook = hasPermission(
      Permission.EditSupportWebhookOutreach,
    );
    const canEditSubscriberPush = hasPermission(
      Permission.EditSubscriberPushOutreach,
    );
    const hasConfigForSubscriber =
      values.outreachConfig[OutreachConfigFormFields.SubscriberOutreach];
    const hasConfigForSupport =
      values.outreachConfig[OutreachConfigFormFields.SupportOutreach];
    const hasWebhookConfig =
      values.outreachConfig[OutreachConfigFormFields.WebhookOutreach];
    const hasConfigForEmail = hasConfigForSupport;
    const hasConfigForWebhook = hasWebhookConfig;
    try {
      setIsSubmitting(true);
      if (hasConfigForEmail && canEditSupportEmail) {
        await handleActionsForSupportConfig(values);
      }
      if (hasConfigForWebhook && canEditSupportWebhook) {
        await handleActionsForSupportWebhookConfig(values);
      }

      if (hasConfigForSubscriber && canEditSubscriberPush) {
        await handleActionsForSubscriberConfig(values);
      }
      await handlePotentialDeleteAction(values);
      params?.onFinalize?.();
    } catch (error) {
      console.error('Error: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    loadingMetadata,
    isError,
    getFormInitialValues,
    onSubmit,
    isSubmitting,
    flushConfig,
  };
};

export default useOutreach;
